import { Overlay } from '@angular/cdk/overlay';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { FormControl } from '@angular/forms';
import {
  combineLatest,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  tap,
  take,
  interval,
} from 'rxjs';
import { CardDesignComponent } from 'src/app/@shared/components/design/slide-up-card/card-design.component';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import { TripCalendarItem } from 'src/app/modules/route-planner/components/route-planner-results/results/search-line/search-line-daily-calendar/search-line-daily-calendar.component';
import { SearchLineResultMapComponent } from 'src/app/modules/route-planner/components/route-planner-results/results/search-line/search-line-result-map/search-line-result-map.component';
import {
  BOARDING_STATION,
  DAILY_CALENDAR,
  DROP_OFF_STATION,
  LINE_ROUTE,
  ROUTE_PLANNING,
  SHARE,
  SHOW_ON_MAP,
  STATION_CODE,
  ROUTE_DETAILS,
  SINGILE_RIDE,
  DAILY_FREE,
  MONTHLY_FREE,
  DATE,
  TIME,
  ADD_TO_FAVORITES,
  MORE_ACTIONS_MENU,
  PRINT,
  PRINT_ARIA_LABEL,
  PLATFORM,
  REMOVE_FROM_FAVORITES,
  SUBMIT_COMPLAIN,
  SEARCH_BY_LINE,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eFavoriteType } from 'src/app/modules/route-planner/enums/favorite/favorite-type.enum';
import { FavoriteLines } from 'src/app/modules/route-planner/interfaces/favorites/favorite-lines.interface';
import { SearchLineForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/search-line-form';
import { Route } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/route.interface';
import { Stop } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/stop.interface';
import { TariffProfile } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/tariff-profile.interface';
import { Price } from 'src/app/modules/route-planner/interfaces/route-planning/price.interface';
import { HourPipe } from 'src/app/modules/route-planner/pipes/hour.pipe';
import { CalendarService } from 'src/app/modules/route-planner/services/calendar/calendar.service';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { ScheduleService } from 'src/app/modules/route-planner/services/schedule/schedule.service';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { SearchLineDetailedResultComponent } from 'src/app/modules/route-planner/components/route-planner-results/results/search-line/search-line-detailed-result/search-line-detailed-result.component';
import { SearchLineDailyCalendarComponent } from 'src/app/modules/route-planner/components/route-planner-results/results/search-line/search-line-daily-calendar/search-line-daily-calendar.component';
import { TripStopTime } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/trip-stop-time.interface';
import { Direction } from '@angular/cdk/bidi';
import { NextDayOfService } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/next-day-of-service.interface';
import { environment } from 'src/environments/environment';
import { PrintService } from 'src/app/modules/route-planner/services/print/print.service';

@Component({
    selector: 'app-search-line-results-container',
    templateUrl: './search-line-results-container.component.html',
    styleUrls: ['./search-line-results-container.component.scss'],
    providers: [HourPipe],
    standalone: false
})
export class SearchLineResultsContainerComponent
  extends CardDesignComponent
  implements OnInit, AfterViewInit {
  compareStopsByCode = (a: Stop, b: Stop): boolean => {
    if (!a || !b) {
      return a === b;
    }
    return a.stopcode === b.stopcode;
  };

  private findNextDropoffStop(
    boarding: Stop,
    stops: Stop[] = this.data.line?.stops ?? []
  ): Stop | null {
    return (
      stops.find(
        (s) => s.stopSequence > boarding.stopSequence && !s.isDropoff
      ) ?? null
    );
  }

  private resolveDropoffAfterBoarding(
    boarding: Stop,
    preferred: Stop | null,
    stops: Stop[]
  ): Stop | null {
    if (
      preferred &&
      preferred.stopSequence > boarding.stopSequence &&
      !preferred.isDropoff
    ) {
      return preferred;
    }
    return (
      this.findNextDropoffStop(boarding, stops) ??
      preferred ??
      stops[stops.length - 1] ??
      null
    );
  }

  private updateStationFilterObservables(
    boardingStop: Stop,
    dropingStop: Stop
  ): void {
    this.dropingStops$ = this.stops$.pipe(
      map((stops) =>
        stops.filter(
          (stop) =>
            stop.value.stopSequence > boardingStop.stopSequence &&
            !stop.value.isDropoff
        )
      )
    );

    this.boardingStops$ = this.stops$.pipe(
      map((stops) =>
        stops.filter(
          (stop) =>
            stop.value.stopSequence < dropingStop.stopSequence &&
            stop.value.stopSequence !== 0 &&
            !stop.value.isPickup
        )
      )
    );
  }

  profile: TariffProfile;
  routePrices: Price[] = [];
  showRoutePrice: boolean = false;

  calendarDay: Date;
  dailyCalendar: TripCalendarItem[] = [];
  isLoadingCalendarData: boolean = false
  calendarSubscription: Subscription;
  private refreshCalendarIntervalSubscription: Subscription;
  /** Date currently awaiting GetRouteStops' stop-list reconciliation before its tab's own calendar refresh (getTripCalendar/getRouteDailyCalendar) may run. */
  private pendingRouteStopsDate: Date | null = null;
  /** Whether GetRouteStops found the line's stops changed for the currently selected date; drives the warning message in both tabs. */
  stopsChangedForSelectedDate: boolean = false;


  nextDayOfService: NextDayOfService = null

  isFavoriteLine: boolean = false;

  stops$: Observable<SelectOption<Stop>[]> = of([]);
  boardingStops$: Observable<SelectOption<Stop>[]> = of([]);
  dropingStops$: Observable<SelectOption<Stop>[]> = of([]);
  /** Snapshot of the line's stops as loaded on init; used only to detect stop-list drift for the warning message, never mutated. */
  private originalStops: Stop[] = [];

  boardingStationFormControl = new FormControl<Stop>(null);
  dropingStationFormControl = new FormControl<Stop>(null);

  moreActionsMenuItems: SelectOption<string>[] = [];

  routePlanningText: string;
  shareText: string;
  lineRouteText: string;
  dailyCalendarText: string;
  showOnMapText: string;
  boardingStationText: string;
  dropOffStationText: string;
  stationCodeText: string;
  platformText: string;
  printText: string;
  printAriaLabelText: string;
  submitComplainText: string;
  moreActionsMenuText: string;
  addToFavoritesText: string;
  removeFromFavoritesText: string;

  direction: Direction

  isNavigatingToRoute: boolean = false;

  @ViewChild(SearchLineDetailedResultComponent)
  searchLineDetailedResult: SearchLineDetailedResultComponent;

  @ViewChild(SearchLineDailyCalendarComponent)
  searchLineDailyCalendar: SearchLineDailyCalendarComponent;

  @ViewChild(MatTabGroup)
  tabGroup: MatTabGroup;

  constructor(
    public override routePlanningService: RoutePlanningService,
    resizeService: ResizeService,
    overlay: Overlay,

    @Inject(DATA_INJECTION_TOKEN)
    public readonly data: {
      formValues: SearchLineForm;
      line: Route;
    },

    private readonly favoritesService: FavoritesService,
    private readonly modalService: ModalService,
    private readonly utilsService: UtilsService,
    private readonly calendarService: CalendarService,
    private readonly languageService: LanguageService,
    private readonly hourPipe: HourPipe,
    private readonly scheduleService: ScheduleService,
    private readonly queryParamsService: QueryParamsService,
    private readonly printService: PrintService
  ) {
    super(routePlanningService, resizeService, overlay);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Initialize properties from input data first
    const line = this.data.line;
    // Check if this is a favorite line
    this.isFavoriteLine =
      !!this.favoritesService.favorites?.lines?.routes?.find(
        (favoriteRoute) => favoriteRoute.routeId === line.routeId
      );

    // Snapshot of the stops as they were on load, kept only to detect (for the warning message)
    // whether GetRouteStops' result has drifted from the original line, regardless of whatever
    // the currently-active stops list has since been changed to by earlier date picks.
    this.originalStops = line.stops;

    // Initialize static data
    this.stops$ = of(
      line.stops.map((stop) => ({ label: stop.name[this.languageService.getSelectedLanguage().symbol] || stop.name.he || '', value: stop }))
    );

    this.dropingStops$ = this.stops$
    this.boardingStops$ = this.stops$

    // Setup multi-language texts
    this.initializeMultiLanguageTexts();

    // Load specialMessages for stops
    this.loadStopsSpecialMessages();

    // Initialize form controls with their initial values before setting up value change subscriptions
    // This prevents unnecessary API calls during initialization
    if (line.stops && line.stops.length) {
      this.boardingStationFormControl.setValue(
        line.stops.find(
          (stop) =>
            stop.stopcode === this.data.formValues.boardingStation?.stopcode
        ) || null,
        { emitEvent: false }
      );

      this.dropingStationFormControl.setValue(
        line.stops.find(
          (stop) =>
            stop.stopcode === this.data.formValues.dropingStation?.stopcode && stop.stopSequence !== 1
        ) || null,
        { emitEvent: false }
      );
    }

    // Now set up value change subscriptions after the initial values are set
    combineLatest([
      this.boardingStationFormControl.valueChanges,
      this.dropingStationFormControl.valueChanges,
    ])
      .pipe(
        tap(() => (this.isLoadingData = true)),
        switchMap(([boardingStop, dropingStop]) => {
          if (!boardingStop || !dropingStop) {
            return of({
              boardingStop,
              dropingStop,
              prices: [],
            });
          }

          return this.scheduleService
            .getRoutePrice({
              routeId: this.data.line.routeId,
              startingStopId: boardingStop.stopid,
              endingStopId: dropingStop.stopid,
            })
            .pipe(
              map((res) => ({
                boardingStop,
                dropingStop,
                prices: res.data || [],
              }))
            );
        })
      )
      .subscribe(({ boardingStop, dropingStop, prices }) => {
        if (
          boardingStop &&
          dropingStop &&
          boardingStop.stopSequence >= dropingStop.stopSequence
        ) {
          const nextDrop = this.resolveDropoffAfterBoarding(
            boardingStop,
            dropingStop,
            this.data.line.stops
          );
          if (nextDrop && nextDrop.stopid !== dropingStop.stopid) {
            this.dropingStationFormControl.setValue(nextDrop);
            return;
          }
        }

        this.routePlanningService.searchLineMapData$.next({
          line: this.data.line,
          boardingStation: this.boardingStationFormControl.value ?? null,
          dropingStation: this.dropingStationFormControl.value ?? null,
        });

        this.routePrices = prices;
        if (boardingStop) {
          this.queryParamsService.setQueryParam(
            'boardingStation',
            boardingStop.stopcode
          );

          if (dropingStop) {
            this.queryParamsService.setQueryParam(
              'dropingStation',
              dropingStop.stopcode
            );
          }
        }

        if (boardingStop && dropingStop) {
          this.updateStationFilterObservables(boardingStop, dropingStop);
          this.getRouteDailyCalendar(this.calendarDay);
        } else {
          this.isLoadingData = false;
        }
      });

    this.boardingStationFormControl.valueChanges.subscribe(() => {
      if (this.boardingStationFormControl.value && this.dropingStationFormControl.value) {
        this.refreshTripCalendarWithChildDate();
      }
    });

    // Refresh trip calendar from child date every minute
    if (this.refreshCalendarIntervalSubscription) {
      this.refreshCalendarIntervalSubscription.unsubscribe();
    }
    // this.refreshCalendarIntervalSubscription = interval(60000).subscribe(() =>
    //   this.refreshTripCalendarWithChildDate()
    // );

      

    // Trigger initial price load if both stations are set
    const initialBoardingStation = this.boardingStationFormControl.value;
    const initialDropingStation = this.dropingStationFormControl.value;

    if (initialBoardingStation && initialDropingStation) {
      this.scheduleService
        .getRoutePrice({
          routeId: this.data.line.routeId,
          startingStopId: initialBoardingStation.stopid,
          endingStopId: initialDropingStation.stopid,
        })
        .subscribe({
          next: (res) => {
            this.routePrices = res.data || [];
          },
          error: () => {},
        });
    }

    // Wait for ResizeableComponent to set isMobile property before opening the map
    this.resizeService.onResize$
      .pipe(
        tap(() => this.openResultsMap()),
        take(1)
      )
      .subscribe();

    if (this.queryParamsService.currentParams['showCalendar'] === 'true') {
      setTimeout(() => {
        if (this.tabGroup) {
          this.tabGroup.selectedIndex = 1;
        }
      });
      this.queryParamsService.setQueryParam('showCalendar', null);
    }
  }

  ngAfterViewInit(): void {
    // setTimeout(() => this.loadInitialTripCalendar(), 0);
  }

  /** Load stop times once on open without flashing the full-page loader. */
  private loadInitialTripCalendar(): void {
    const calendarPick = this.searchLineDetailedResult?.calendarPickForm?.value;
    if (calendarPick?.day != null && calendarPick?.time != null) {
      this.getTripCalendar(
        { day: calendarPick.day, time: calendarPick.time },
        false
      );
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy?.();
    this.refreshCalendarIntervalSubscription?.unsubscribe();
  }

  openResultsMap() {
    if (!this.isMobile) {
      const data = {
        line: this.data.line,
        boardingStation: this.boardingStationFormControl.value ?? null,
        dropingStation: this.dropingStationFormControl.value ?? null,
      };
      this.routePlanningService._mapOverlaySub.next({
        mapComponent: SearchLineResultMapComponent,
        data,
      });
      this.routePlanningService.searchLineMapData$.next(data);
    }
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      if(this.direction) {
      
        this.handleCloseClick(true);
        return;
      }
      this.direction = language.direction
      this.routePlanningText = SEARCH_BY_LINE[language.language];
      this.shareText = SHARE[language.language];
      this.lineRouteText = LINE_ROUTE[language.language];
      this.dailyCalendarText = DAILY_CALENDAR[language.language];
      this.showOnMapText = SHOW_ON_MAP[language.language];
      this.boardingStationText = BOARDING_STATION[language.language];
      this.dropOffStationText = DROP_OFF_STATION[language.language];
      this.stationCodeText = STATION_CODE[language.language];
      this.platformText = PLATFORM[language.language];
      this.printText = PRINT[language.language];
      this.printAriaLabelText = PRINT_ARIA_LABEL[language.language];
      this.submitComplainText = SUBMIT_COMPLAIN[language.language];
      this.moreActionsMenuText = MORE_ACTIONS_MENU[language.language];
      this.addToFavoritesText = ADD_TO_FAVORITES[language.language];
      this.removeFromFavoritesText = REMOVE_FROM_FAVORITES[language.language];
      this.buildMoreActionsMenuItems();
    });
  }

  toggleFavoriteRoute() {
    const favoriteLines = this.favoritesService.favorites?.lines;
    const newLines = favoriteLines?.routes ? [...favoriteLines?.routes] : [];

    if (!this.isFavoriteLine) {
      newLines.push({
        ...this.data.line,
        type: eFavoriteType.LINES,
      });
    } else {
      newLines.splice(
        newLines.findIndex((route) => route.routeId == this.data.line.routeId),
        1
      );
    }

    const newFavoriteLines: FavoriteLines = {
      ...favoriteLines,
      routes: newLines,
      type: eFavoriteType.LINES,
    };

    this.favoritesService.updateFavorite(newFavoriteLines);

    this.isFavoriteLine = !this.isFavoriteLine;
    this.buildMoreActionsMenuItems();
  }

  private buildMoreActionsMenuItems(): void {
    this.moreActionsMenuItems = [
      {
        label: this.isFavoriteLine ? this.removeFromFavoritesText : this.addToFavoritesText,
        value: 'favorite',
        icon: this.isFavoriteLine ? 'heart-filled' : 'heart',
        ariaLabel: this.isFavoriteLine ? this.removeFromFavoritesText : this.addToFavoritesText,
        onClick: () => this.toggleFavoriteRoute(),
      },
      {
        label: this.printText,
        value: 'print',
        icon: 'print',
        ariaLabel: this.printAriaLabelText,
        onClick: () => this.handlePrint(),
      },
      {
        label: this.submitComplainText,
        value: 'contact',
        icon: 'circle-exclamation',
        ariaLabel: this.submitComplainText,
        onClick: () => this.handleSubmitRequestClick(),
      },
    ];
  }

  openShareLinkPopup() {
    if (this.data && this.data.line) {
      console.debug('Preserving line information for sharing:', this.data.line);

      // Create a complete set of query parameters
      const queryParams: any = {
        line: this.data.line.routeName,
        routeId: this.data.line.routeId,
        oppositeRouteId: this.data.line.oppositeDirectionId,
      };

      if (this.boardingStationFormControl.value) {
        queryParams.boardingStation =
          this.boardingStationFormControl.value.stopcode;
      }

      if (this.dropingStationFormControl.value) {
        queryParams.dropingStation =
          this.dropingStationFormControl.value.stopcode;
      }

      // Apply all parameters at once
      this.queryParamsService.setQueryParams(queryParams);

      // Wait a moment to ensure parameters are applied
      setTimeout(() => {
        this.modalService.openShareLinkModal();
      }, 100);
    } else {
      this.modalService.openShareLinkModal();
    }
  }

  onDetailedResultDateChange(date: Date) {
    this.syncDailyCalendarDate(date);
    this.pendingRouteStopsDate = date;
    this.fetchRouteStopsThenRefresh(date, 'detailed');
  }

  onDailyCalendarDateChange(date: Date) {
    this.syncDetailedResultDate(date);
    this.pendingRouteStopsDate = date;
    this.fetchRouteStopsThenRefresh(date, 'daily');
  }

  /**
   * Time-only calendarPickForm changes still refresh the trip calendar immediately here.
   * Date changes are instead handled by fetchRouteStopsThenRefresh once GetRouteStops resolves,
   * so they're skipped here to avoid calling GetTripCalendar with a boarding station GetRouteStops
   * might still be about to update.
   */
  onCalendarPickChangeHandler(calendarDate: { day: Date; time: string }) {
    if (
      this.pendingRouteStopsDate &&
      this.pendingRouteStopsDate.getTime() === calendarDate.day.getTime()
    ) {
      return;
    }
    this.getTripCalendar(calendarDate);
  }

  /** Mirrors the date into the other tab's date control without re-emitting its valueChanges, so its own date-change effects (and the shared getRouteStops call) don't fire a second time. */
  private syncDailyCalendarDate(date: Date): void {
    const control = this.searchLineDailyCalendar?.dayFormControl;
    if (control && control.value?.getTime() !== date?.getTime()) {
      control.setValue(date, { emitEvent: false });
    }
  }

  private syncDetailedResultDate(date: Date): void {
    const control = this.searchLineDetailedResult?.calendarPickForm?.get('day');
    if (control && control.value?.getTime() !== date?.getTime()) {
      control.setValue(date, { emitEvent: false });
    }
  }

  /** Runs GetRouteStops and its full stop-list reconciliation to completion first, then triggers the requesting tab's own calendar refresh using the boarding/dropping stations that reconciliation resolved. */
  private fetchRouteStopsThenRefresh(date: Date, source: 'detailed' | 'daily'): void {
    this.scheduleService
      .getRouteStops(this.data.line.routeId, this.data.line.oppositeDirectionId, date)
      .subscribe((res) => {
        const newStops = res.data?.route?.stops;
        if (newStops) {
          this.syncStopsAfterRouteStopsChange(newStops);
        }
        this.pendingRouteStopsDate = null;

        if (source === 'detailed') {
          const time = this.searchLineDetailedResult?.calendarPickForm?.get('time')?.value;
          if (time != null) {
            this.getTripCalendar({ day: date, time });
          }
        } else {
          this.getRouteDailyCalendar(date);
        }
      });
  }

  private stopsListsAreEqual(a: Stop[], b: Stop[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const sortedA = [...a].sort((x, y) => x.stopSequence - y.stopSequence);
    const sortedB = [...b].sort((x, y) => x.stopSequence - y.stopSequence);
    return sortedA.every(
      (stop, i) =>
        stop.stopid === sortedB[i].stopid &&
        stop.stopSequence === sortedB[i].stopSequence
    );
  }

  private syncStopsAfterRouteStopsChange(newStops: Stop[]): void {
    // Warning message always compares against the original, as-loaded stop list -
    // not the currently-active one - so it keeps showing across consecutive date
    // picks whose stops all still differ from the original, even if they match each other.
    this.stopsChangedForSelectedDate = !this.stopsListsAreEqual(
      this.originalStops,
      newStops
    );

    const currentStops = this.data.line.stops ?? [];
    if (this.stopsListsAreEqual(currentStops, newStops)) {
      return;
    }

    const sortedNewStops = [...newStops].sort(
      (a, b) => a.stopSequence - b.stopSequence
    );
    this.data.line.stops = sortedNewStops;

    this.stops$ = of(
      sortedNewStops.map((stop) => ({
        label:
          stop.name[this.languageService.getSelectedLanguage().symbol] ||
          stop.name.he ||
          '',
        value: stop,
      }))
    );

    const currentBoardingStation = this.boardingStationFormControl.value;
    const currentDropingStation = this.dropingStationFormControl.value;

    const boardingStillPresent = sortedNewStops.some(
      (s) => s.stopid === currentBoardingStation?.stopid
    );
    const dropingStillPresent = sortedNewStops.some(
      (s) => s.stopid === currentDropingStation?.stopid
    );

    const newBoardingStation = boardingStillPresent
      ? sortedNewStops.find((s) => s.stopid === currentBoardingStation.stopid) ?? null
      : sortedNewStops[0] ?? null;
    const newDropingStation = dropingStillPresent
      ? sortedNewStops.find((s) => s.stopid === currentDropingStation.stopid) ?? null
      : sortedNewStops[sortedNewStops.length - 1] ?? null;

    const removedBoardingStop =
      !boardingStillPresent && currentBoardingStation ? currentBoardingStation : null;
    const removedDropingStop =
      !dropingStillPresent && currentDropingStation ? currentDropingStation : null;

    if (removedBoardingStop || removedDropingStop) {
      this.modalService.openStopsChangedModal(removedBoardingStop, removedDropingStop);
    }

    if (newBoardingStation && newDropingStation) {
      this.updateStationFilterObservables(newBoardingStation, newDropingStation);
    }

    this.boardingStationFormControl.setValue(newBoardingStation);
    this.dropingStationFormControl.setValue(newDropingStation);
  }

  getRouteDailyCalendar(day: Date) {
    this.isLoadingData = true;
    this.isLoadingCalendarData = true
    if (this.calendarDay !== day) {
      this.calendarDay = day;
    }
    const boardingStationId = this.boardingStationFormControl.value.stopid;
    const dropingStationId = this.dropingStationFormControl.value.stopid;

    if (!!this.calendarSubscription) this.calendarSubscription.unsubscribe();

    this.calendarSubscription = this.calendarService
      .GetRouteDailyCalendar({
        startStopId: boardingStationId,
        endStopId: dropingStationId,
        routeId: this.data.line.routeId,
        day: day.toISOString(),
      })
      .subscribe((res) => {
        const calendars = res.data;
        const boardingStationStopTimes = calendars["start"];
        const dropingStationStopTimes = calendars["end"];

        this.dailyCalendar = boardingStationStopTimes?.map(
          (stopTime, index) => {
            const arrivalTime = this.hourPipe.transform(stopTime.departureTimeString);
            const endStopTime = dropingStationStopTimes[index];
            return {
              arrivalTime: arrivalTime,
              departureTime: this.hourPipe.transform(endStopTime?.departureTimeString),
              crowdedness: stopTime.crowdedness,
              isNextDay: endStopTime?.isNextDay ?? false,
            };
          }
        );
        this.isLoadingData = false;
        this.isLoadingCalendarData = false

        if(!boardingStationStopTimes.length && !dropingStationStopTimes.length) {
          this.isLoadingData = true;
          this.isLoadingCalendarData = true
          this.calendarService.GetRouteNextDayOfService({
            routeId: this.data.line.routeId,
            day: day.toISOString(),
            boardingStationId,
            dropingStationId,
          }).subscribe((res) => {
            this.nextDayOfService = res.data
            this.isLoadingData = false;
            this.isLoadingCalendarData = false
          })
        }
      });
  }

  /**
   * When boarding/dropping station changes, trigger getTripCalendar with the date and time
   * currently selected in the detailed result (calendar pick) form.
   */
  refreshTripCalendarWithChildDate(): void {
    setTimeout(() => {
      const calendarPick = this.searchLineDetailedResult?.calendarPickForm?.value;
      if (calendarPick?.day != null && calendarPick?.time != null) {
        this.getTripCalendar({ day: calendarPick.day, time: calendarPick.time }, false);
      }
    }, 0);
  }

  getTripCalendar(calendarDate: { day: Date; time: string }, showLoadingIndicator: boolean = true) {
    if (showLoadingIndicator) {
    this.isLoadingData = true;
    }
    const boardingStation = this.boardingStationFormControl.value;

    const dateTime = new Date(calendarDate.day);
    const [hours, minutes] = calendarDate.time.split(':').map(Number);
    dateTime.setHours(hours, minutes, 0, 0);

    this.calendarService
      .GetTripCalendar({
        routeId: this.data.line.routeId,
        stopId: boardingStation?.stopid,
        stopSequence: boardingStation?.stopSequence,
        date: dateTime.toISOString(),
      })
      .subscribe((val) => {
        const calendar = val.data;

        if (!!calendar && Object.keys(calendar).length > 0) {
          const stopsFromCalendar = this.createNewLineStops(calendar);
          this.data.line.stops = [...stopsFromCalendar];
          const viewedStopsId = []
          for (let i = 0; i < this.data.line.stops.length; i++) {
            const stop = this.data.line.stops[i];
            const stopCalendar = calendar[stop.stopid];
            if (stopCalendar.length === 1) {
              stop.nextStopTime = calendar[stop.stopid][0];
            } else {
              if (viewedStopsId.find(id => id === stopCalendar[0].stopid)) {
                stop.nextStopTime = calendar[stop.stopid][1]
              } else {
                stop.nextStopTime = calendar[stop.stopid][0]
              }
            }
            viewedStopsId.push(stop.stopid)

          }
          this.setNewLineStopsOptions();
          // Reload special messages with date and time parameters
          this.loadStopsSpecialMessages(
            calendarDate.day.getDay(),
            calendarDate.time
          );
          const boardingCalendar =
            boardingStation?.stopid != null
              ? calendar[boardingStation.stopid]
              : undefined;
          const tripId = boardingCalendar?.[0]?.tripId;
          if (tripId) {
          this.scheduleService.getTripShapePolyline(tripId).subscribe( { 
            next: (res) => {
              this.data.line = {...this.data.line, polyline: res.data}
              this.routePlanningService.searchLineMapData$.next({
                line: this.data.line,
                boardingStation: this.boardingStationFormControl.value ?? null,
                dropingStation: this.dropingStationFormControl.value ?? null,
              });
              this.isLoadingData = false;
            },
            error: (err) => {
              this.isLoadingData = false;
            }
            })
          } else {
            this.isLoadingData = false;
          }
        } else {
          this.data.line.stops.forEach(stop => {
            stop.nextStopTime = null;
          });
          this.isLoadingData = false;
        }
      });
  }

  handlePrint() {
    const lineHeader = document.querySelector<HTMLElement>('.print-line-header');
    const stationInfoElements = Array.from(document.querySelectorAll<HTMLElement>('.print-station-info'));

    // The two tabs render different components; only one is meant to be printed, whichever the user is
    // currently looking at. Falls back to the line-route tab for the mobile-only map tab (index 2),
    // since a map isn't something we print anyway.
    const tabContentSelector =
      this.tabGroup?.selectedIndex === 1 ? 'app-search-line-daily-calendar' : 'app-search-line-detailed-result';
    const tabContent = document.querySelector<HTMLElement>(tabContentSelector);

    if (!lineHeader || !tabContent) {
      console.warn('Printable content not found!');
      return;
    }

    const currentLanguage = this.languageService.getSelectedLanguage().language;
    this.printService.print([lineHeader, ...stationInfoElements, tabContent], {
      title: ROUTE_DETAILS[currentLanguage],
    });
  }

  createNewLineStops(calendar: {
    [stopId: number]: TripStopTime[];
  }) {
    const stopsFromCalendar: Stop[] = [];

    // Iterate through each stopId in the calendar
    Object.keys(calendar).forEach(stopId => {
      const stopTimes = calendar[parseInt(stopId)];
     

      // Create a Stop object for each TripStopTime in the array
      stopTimes.forEach(stopTime => {
        const originalStop = this.data.line.stops.find(s => s.stopid === parseInt(stopId));
       
        const newStop: Stop = {
          stopid: parseInt(stopId),
          stopcode: stopTime.stopcode || 0,
          stopSequence: stopTime.stopSequence || 0,
          isDropoff: stopTime.isDropoff || false,
          isPickup: stopTime.isPickup || false,
          name: stopTime.name,
          desc: originalStop?.desc || '',
          cityName: originalStop?.cityName || '',
          streetName: originalStop?.streetName || '',
          streetNumber: originalStop?.streetNumber || '',
          specialMessages: originalStop?.specialMessages || [],
          nextStopTime: stopTime,
          lat: stopTime.lat,
          lng: stopTime.lng,
          platformNumber: stopTime.platformNumber
        };

      
        stopsFromCalendar.push(newStop);
      });
    });

    return stopsFromCalendar.sort((a, b) => a.stopSequence - b.stopSequence);
  }

  setNewLineStopsOptions() {
    // Fix: Set form controls BEFORE updating the stops$ and filtered observables,
    // so that the filters have the correct values to work with.

    const currentBoardingStation = this.boardingStationFormControl.value;
    const currentDropingStation = this.dropingStationFormControl.value;

    const newBoardingStation =
      this.data.line.stops?.find(
        (s) => s.stopid === currentBoardingStation?.stopid
      ) || this.data.line.stops[0];
    const newDropingStation = this.resolveDropoffAfterBoarding(
      newBoardingStation,
      this.data.line.stops?.find(
        (s) =>
          s.stopid === currentDropingStation?.stopid && s.stopSequence !== 1
      ) ?? null,
      this.data.line.stops
    );

    this.boardingStationFormControl.setValue(newBoardingStation, {
      emitEvent: false,
    });
    this.dropingStationFormControl.setValue(newDropingStation, {
      emitEvent: false,
    });

    this.stops$ = of(
      this.data.line.stops.map((stop) => ({
        label:
          stop.name[this.languageService.getSelectedLanguage().symbol] ||
          stop.name.he ||
          '',
        value: stop,
      }))
    );

    if (newBoardingStation && newDropingStation) {
      this.updateStationFilterObservables(newBoardingStation, newDropingStation);
    }
  }

  loadStopsSpecialMessages(dayPassingAtStop?: number, timePassingAtStop?: string) {
    if (!this.data.line.stops || this.data.line.stops.length === 0) {
      return;
    }

    const stopCodes = this.data.line.stops
      .map(stop => stop.stopcode?.toString())
      .filter(code => code && code !== '0');

    if (stopCodes.length === 0) {
      return;
    }

    this.scheduleService.getSpecialMessageByStopCodes(stopCodes, dayPassingAtStop, timePassingAtStop).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          response.data.forEach((stopData) => {
            const stop = this.data.line.stops.find(s => s.stopid === stopData.stopId);
            if (stop) {
              // Merge with existing specialMessages if any
              stop.specialMessages = stop.specialMessages || [];
              stop.specialMessages = [...stop.specialMessages, ...stopData.specialMessages];
            }
          });
        }
      },
      error: (err) => {
        console.error('Error loading special messages for stops:', err);
      }
    });
  }
  openAgencyContact(isForOrderSeat: boolean, agencyId: number) {
    this.modalService.openAgencyContactInfoModal(agencyId, isForOrderSeat)
  }
  handleCloseClick(isLanguageDirectionChanged: boolean) {
      this.routePlanningService._mapOverlaySub.next(null);
      this.closeCard(isLanguageDirectionChanged);
    
  }

  getStopName(stop: Stop): string {
    return stop.name[this.languageService.getSelectedLanguage().symbol] || stop.name.he || stop.name || '';
  }

  override handleOverlayEscape(): void {
    this.handleCloseClick(false);
   }

   handleSubmitRequestClick(): void {
    const url = this.languageService.getSelectedLanguage().symbol === 'he' ? environment.complaintUrl : environment.complaintUrlMultiLanguage;
    window.open(url, '_blank');
  }
}
